import * as THREE from 'three';
import { NacelleComponentInfo } from '../types';

export const NACELLE_COMPONENTS: Record<string, NacelleComponentInfo> = {
  mainShaft: {
    id: 'mainShaft',
    name: '低速主軸 (Main Shaft)',
    nameEn: 'Low-Speed Main Shaft',
    role: '傳遞風力轉矩',
    energyFlow: '空氣動力學風能 ➔ 轉子旋轉機械能',
    details: '直接連接葉片輪轂，承受強大空氣動力彎矩與轉矩。轉速極低（約 6 ~ 15 RPM），但傳遞數兆瓦級的超高扭力。',
    specs: '轉速：約 8-14 RPM | 材質：高強度鍛造合金鋼'
  },
  mainBearing: {
    id: 'mainBearing',
    name: '主軸承 (Main Bearing)',
    nameEn: 'Main Bearing System',
    role: '支撐重載主軸',
    energyFlow: '支撐徑向與軸向載荷，確保旋轉平穩',
    details: '安裝在機艙底座前段，承擔轉子與主軸的全部自重及強烈海風帶來的動態負載，保證數十年運轉可靠性。',
    specs: '類型：雙列球面滾子軸承 | 潤滑：全自動加壓注脂系統'
  },
  gearbox: {
    id: 'gearbox',
    name: '増速齒輪箱 (Gearbox)',
    nameEn: 'Speed-Up Planetary Gearbox',
    role: '扭力與轉速匹配',
    energyFlow: '低速大扭矩 ➔ 高速低扭矩（増速約 90~120 倍）',
    details: '傳統齒輪傳動型風機的核心關鍵樞紐。將主軸約 12 RPM 的緩慢轉速，透過行星齒輪與平行級歯輪升速至約 1,500 RPM，以符合發電機高效發電需求。',
    specs: '傳動比：約 1:100 | 結構：一級行星 + 兩級平行軸齒輪'
  },
  brake: {
    id: 'brake',
    name: '機械制動煞車 (Brake)',
    nameEn: 'High-Speed Mechanical Brake',
    role: '安全鎖定與緊急停機',
    energyFlow: '緊急制動，防止超速損壞',
    details: '安裝在高速軸端，正常運轉時處於釋放狀態。在極端暴風、電網跳脫或人員登頂維修時，配合葉片順槳（Pitching）將轉子安全鎖定。',
    specs: '類型：液壓盤式煞車卡鉗 | 反應時間：< 2.5 秒'
  },
  highSpeedShaft: {
    id: 'highSpeedShaft',
    name: '高速軸 (High-Speed Shaft)',
    nameEn: 'High-Speed Flexible Shaft',
    role: '傳遞高速旋轉動力',
    energyFlow: '齒輪箱高速輸出 ➔ 驅動發電機轉子',
    details: '轉速高達 1,200 ~ 1,800 RPM，配備彈性聯軸器（Flexible Coupling），可吸收齒輪箱與發電機之間的微小軸向與徑向位移偏差。',
    specs: '轉速：約 1,200 - 1,800 RPM | 配備柔性聯軸器'
  },
  generator: {
    id: 'generator',
    name: '發電機 (Generator)',
    nameEn: 'Electrical Generator',
    role: '電磁感應發電',
    energyFlow: '機械動能 ➔ 三相交流電能 (AC)',
    details: '風電機組能量轉換的心臟！透過高速旋轉的轉子切割定子磁力線，將機械能轉換為 690V 交流電。配備高效水冷或風冷循環散熱系統。',
    specs: '輸出電壓：690V AC | 效率：> 96.5% | 散熱：閉式液冷'
  },
  converter: {
    id: 'converter',
    name: '全功率變頻系統 (Converter)',
    nameEn: 'Full-Power Frequency Converter',
    role: '電力調頻與併網品質控制',
    energyFlow: '不穩定可變頻率電能 ➔ 穩定 50/60Hz 電網交流電',
    details: '採用交-直-交（AC-DC-AC）拓撲。先將隨風速波動的交流電整流為直流電，再透過 IGBT 逆變器轉換為電壓與頻率與台灣電網完全同步的優質純淨電力。',
    specs: '電網頻率：60 Hz (台灣電網標準) | 諧波失真：THD < 3%'
  },
  yawSystem: {
    id: 'yawSystem',
    name: '偏航轉向系統 (Yaw System)',
    nameEn: 'Yaw Drive & Bearing',
    role: '機艙風向追蹤對風',
    energyFlow: '電力驅動 ➔ 旋轉整個機艙對準來風方向',
    details: '位於塔頂與機艙底座連接處。根據風向計回傳訊號，多組偏航馬達帶動巨大外齒圈緩慢轉動機艙，確保葉片隨時垂直迎風，最大化發電效率並減少不均勻疲勞負載。',
    specs: '驅動方式：4~8 組電動行星減速馬達 | 齒圈直徑：約 4.5 米'
  },
  controller: {
    id: 'controller',
    name: '智慧主控制器 (SCADA Controller)',
    nameEn: 'Turbine Control Unit / SCADA',
    role: '全機大腦與安全監控',
    energyFlow: '感測器數據 ➔ 即時運算 ➔ 致動器最佳化控制',
    details: '持續採樣葉片負載、風速、振動、溫度等數百個感知數據，毫秒級自主調節葉片變槳角與發電機反轉矩，並具備遠端連線監控機能。',
    specs: '冗餘工業級 PLC | 採樣頻率：100 Hz | 支援遠距離岸光纖'
  }
};

export class Drivetrain {
  public group: THREE.Group;
  public interactiveMeshes: THREE.Mesh[] = [];
  private componentMeshMap: Map<string, THREE.Mesh> = new Map();
  private animatedShafts: THREE.Object3D[] = [];
  private generatorRotor!: THREE.Mesh;
  private highSpeedShaftMesh!: THREE.Mesh;
  private mainShaftMesh!: THREE.Mesh;

  constructor() {
    this.group = new THREE.Group();
    this.buildInternalComponents();
  }

  private buildInternalComponents() {
    // Standard materials for mechanical cutaway
    const castIronMat = new THREE.MeshStandardMaterial({
      color: 0x475569,
      roughness: 0.6,
      metalness: 0.7
    });

    const steelMat = new THREE.MeshStandardMaterial({
      color: 0x94a3b8,
      roughness: 0.3,
      metalness: 0.85
    });

    const bronzeMat = new THREE.MeshStandardMaterial({
      color: 0xd97706,
      roughness: 0.4,
      metalness: 0.6
    });

    const copperMat = new THREE.MeshStandardMaterial({
      color: 0xb45309,
      roughness: 0.35,
      metalness: 0.8
    });

    const electricalBlueMat = new THREE.MeshStandardMaterial({
      color: 0x0284c7,
      roughness: 0.4,
      metalness: 0.5
    });

    const safetyYellowMat = new THREE.MeshStandardMaterial({
      color: 0xeab308,
      roughness: 0.5,
      metalness: 0.3
    });

    // Bedplate (machine frame base)
    const bedplateGeo = new THREE.BoxGeometry(4.2, 0.45, 13.5);
    const bedplate = new THREE.Mesh(bedplateGeo, castIronMat);
    bedplate.position.set(0, -0.9, -1.5);
    this.group.add(bedplate);

    // 1. Low-Speed Main Shaft
    // Oriented along Z-axis (forward is +Z, towards hub)
    const mainShaftGeo = new THREE.CylinderGeometry(0.52, 0.52, 3.4, 24);
    mainShaftGeo.rotateX(Math.PI / 2);
    this.mainShaftMesh = new THREE.Mesh(mainShaftGeo, steelMat);
    this.mainShaftMesh.position.set(0, 0.4, 2.7);
    this.mainShaftMesh.name = 'mainShaft';
    this.mainShaftMesh.userData = { componentId: 'mainShaft' };
    this.registerInteractive(this.mainShaftMesh);

    // 2. Main Bearing Housing
    const bearingGeo = new THREE.CylinderGeometry(0.92, 0.98, 1.2, 24);
    bearingGeo.rotateX(Math.PI / 2);
    const bearingHousing = new THREE.Mesh(bearingGeo, bronzeMat);
    bearingHousing.position.set(0, 0.4, 2.6);
    bearingHousing.name = 'mainBearing';
    bearingHousing.userData = { componentId: 'mainBearing' };
    this.registerInteractive(bearingHousing);

    // 3. Gearbox Housing
    const gearboxGeo = new THREE.BoxGeometry(2.3, 2.3, 2.6);
    const gearboxMesh = new THREE.Mesh(gearboxGeo, castIronMat);
    gearboxMesh.position.set(0, 0.45, 0.6);
    gearboxMesh.name = 'gearbox';
    gearboxMesh.userData = { componentId: 'gearbox' };
    this.registerInteractive(gearboxMesh);

    // Gearbox Inspection detail (planetary ring flange)
    const flangeGeo = new THREE.TorusGeometry(1.0, 0.12, 12, 32);
    const flangeMesh = new THREE.Mesh(flangeGeo, bronzeMat);
    flangeMesh.position.set(0, 0.45, 1.85);
    gearboxMesh.add(flangeMesh);

    // 4. Mechanical Brake Disc & Caliper
    const brakeDiscGeo = new THREE.CylinderGeometry(0.75, 0.75, 0.12, 32);
    brakeDiscGeo.rotateX(Math.PI / 2);
    const brakeDisc = new THREE.Mesh(brakeDiscGeo, steelMat);
    brakeDisc.position.set(0, 0.45, -0.9);
    brakeDisc.name = 'brake';
    brakeDisc.userData = { componentId: 'brake' };
    this.registerInteractive(brakeDisc);

    const caliperGeo = new THREE.BoxGeometry(0.4, 0.65, 0.35);
    const caliperMat = new THREE.MeshStandardMaterial({ color: 0xdc2626, roughness: 0.4 });
    const caliper = new THREE.Mesh(caliperGeo, caliperMat);
    caliper.position.set(0.65, 0.55, -0.9);
    this.group.add(caliper);

    // 5. High-Speed Shaft
    const hsShaftGeo = new THREE.CylinderGeometry(0.24, 0.24, 1.6, 20);
    hsShaftGeo.rotateX(Math.PI / 2);
    this.highSpeedShaftMesh = new THREE.Mesh(hsShaftGeo, steelMat);
    this.highSpeedShaftMesh.position.set(0, 0.45, -1.6);
    this.highSpeedShaftMesh.name = 'highSpeedShaft';
    this.highSpeedShaftMesh.userData = { componentId: 'highSpeedShaft' };
    this.registerInteractive(this.highSpeedShaftMesh);

    // 6. Generator
    const genGeo = new THREE.CylinderGeometry(1.25, 1.25, 2.8, 28);
    genGeo.rotateX(Math.PI / 2);
    const genMesh = new THREE.Mesh(genGeo, copperMat);
    genMesh.position.set(0, 0.45, -3.8);
    genMesh.name = 'generator';
    genMesh.userData = { componentId: 'generator' };
    this.registerInteractive(genMesh);

    // Generator cooling fins detail
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const finGeo = new THREE.BoxGeometry(0.1, 0.3, 2.7);
      const fin = new THREE.Mesh(finGeo, steelMat);
      fin.position.set(Math.cos(angle) * 1.3, 0.45 + Math.sin(angle) * 1.3, -3.8);
      this.group.add(fin);
    }

    // 7. Full-Power Frequency Converter & Transformer Cabinet
    const converterGeo = new THREE.BoxGeometry(1.8, 1.9, 2.2);
    const converterMesh = new THREE.Mesh(converterGeo, electricalBlueMat);
    converterMesh.position.set(0, 0.3, -6.4);
    converterMesh.name = 'converter';
    converterMesh.userData = { componentId: 'converter' };
    this.registerInteractive(converterMesh);

    // 8. Yaw System (Ring Gear & Drives)
    const yawRingGeo = new THREE.TorusGeometry(1.7, 0.22, 16, 40);
    yawRingGeo.rotateX(Math.PI / 2);
    const yawRing = new THREE.Mesh(yawRingGeo, bronzeMat);
    yawRing.position.set(0, -1.2, -1.2);
    yawRing.name = 'yawSystem';
    yawRing.userData = { componentId: 'yawSystem' };
    this.registerInteractive(yawRing);

    // 4 Yaw Motors around ring
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const motorGeo = new THREE.CylinderGeometry(0.24, 0.24, 0.8, 16);
      const motor = new THREE.Mesh(motorGeo, castIronMat);
      motor.position.set(
        Math.cos(angle) * 1.7,
        -0.8,
        -1.2 + Math.sin(angle) * 1.7
      );
      this.group.add(motor);
    }

    // 9. Controller / SCADA cabinet
    const ctrlGeo = new THREE.BoxGeometry(0.8, 1.4, 0.6);
    const ctrlMesh = new THREE.Mesh(ctrlGeo, safetyYellowMat);
    ctrlMesh.position.set(1.4, 0.2, 0.2);
    ctrlMesh.name = 'controller';
    ctrlMesh.userData = { componentId: 'controller' };
    this.registerInteractive(ctrlMesh);
  }

  private registerInteractive(mesh: THREE.Mesh) {
    this.group.add(mesh);
    this.interactiveMeshes.push(mesh);
    if (mesh.userData.componentId) {
      this.componentMeshMap.set(mesh.userData.componentId, mesh);
    }
  }

  public update(rotorRpm: number, delta: number) {
    // Low speed shaft matches rotor
    const lowSpeedRad = (rotorRpm * 2 * Math.PI / 60) * delta;
    if (this.mainShaftMesh) {
      this.mainShaftMesh.rotation.z += lowSpeedRad;
    }

    // High speed shaft spins ~100x faster!
    const highSpeedRad = lowSpeedRad * 100;
    if (this.highSpeedShaftMesh) {
      this.highSpeedShaftMesh.rotation.z += highSpeedRad;
    }
  }

  public highlightComponent(componentId: string | null) {
    for (const [id, mesh] of this.componentMeshMap.entries()) {
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (id === componentId) {
        mat.emissive = new THREE.Color(0x06b6d4);
        mat.emissiveIntensity = 0.55;
      } else {
        mat.emissive = new THREE.Color(0x000000);
        mat.emissiveIntensity = 0.0;
      }
    }
  }

  public getComponentInfo(id: string): NacelleComponentInfo | null {
    return NACELLE_COMPONENTS[id] || null;
  }
}
